import { useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useRouter } from 'next/router';

import { useAuth } from 'context/Auth';
import { useCampaignJoin, CAMPAIGN_JOIN_ID_VAR } from 'context/CampaignJoin';
import CampaignJoinLayout from 'components/layouts/CampaignJoinLayout';
import EstablishUserForm from 'components/global/EstablishUserForm';
import Button from 'components/common/Button';
import Modal from 'components/common/Modal';
import UserSuccessGraphic from '../../../../svg/graphic-user-success.svg';

const FUNDRAISER_CHECKS = gql`
  query FundraiserChecks($campaignId: String!, $userId: String!) {
    findCampaigns(id: $campaignId) {
      id
      fundraisers(where: { userId: $userId }) {
        id
        teamId
      }
    }
  }
`;

const CampaignJoinRoute = () => {
  const router = useRouter();

  const { user, logout } = useAuth();
  const { campaign, modules, formData, onNavigate } = useCampaignJoin();
  const [fundraiser, setFundraiser] = useState(null);

  // This query will get run immediately after establishing a user
  useQuery(FUNDRAISER_CHECKS, {
    variables: {
      campaignId: campaign.id,
      userId: user?.id,
    },
    skip: !user,
    onCompleted: ({ findCampaigns }) => {
      const existingFundraiser = findCampaigns[0]?.fundraisers[0];
      const joiningTeamId = formData.teamId;

      // If there's an existing fundraiser and they're trying to join a team,
      // redirect back to let the team join page handle the request
      if (existingFundraiser && joiningTeamId) {
        router.replace(`/t/${joiningTeamId}/join`);
        return;
      }

      // If there's an existing fundraiser, display the revisit modal
      if (existingFundraiser) {
        setFundraiser(existingFundraiser);
        return;
      }

      if (modules.tickets) {
        onNavigate('tickets');
        return;
      }

      if (modules.team && !joiningTeamId) {
        onNavigate('teams');
        return;
      }

      onNavigate('fundraiserCreate');
    },
  });

  return (
    <>
      {fundraiser && (
        <Modal onHide={() => router.replace(`/c/${campaign.id}`)} className="md:max-w-sm" show>
          <div className="text-center p-6 sm:p-8 space-y-5">
            <UserSuccessGraphic className="block mx-auto" />
            <p className="text-lg sm:text-xl font-medium text-center">
              You have already joined this campaign.
            </p>
            <div className="space-y-3">
              <Button
                as="button"
                type="button"
                onClick={() => router.replace(`/f/${fundraiser.id}`)}
                color="primary"
                className="w-full font-medium"
              >
                Go to my page
              </Button>
              <Button
                as="button"
                type="button"
                onClick={() => {
                  logout();
                  setFundraiser(null);
                }}
                color="gray-300"
                className="w-full font-medium"
                outline
              >
                Sign out
              </Button>
            </div>
          </div>
        </Modal>
      )}
      <EstablishUserForm onContinue={() => {}} />
    </>
  );
};

CampaignJoinRoute.propTypes = {};

CampaignJoinRoute.defaultProps = {};

CampaignJoinRoute.getLayout = (page, router) => {
  CAMPAIGN_JOIN_ID_VAR(router.query.id);
  return <CampaignJoinLayout>{page}</CampaignJoinLayout>;
};

export default CampaignJoinRoute;
